import { useEffect, useState } from "react";
import { factLabel, valueLabel } from "./proveFactLabels";

/**
 * 필수·배제 카드(2026-09-25 사용자 요청·시안 확인) — 지도에서 고른 세번이 되려면 맞아야 하는 조건(필수)과, 하나라도 해당하면
 * 다른 곳으로 가는 조건(배제 → 도착 세번)을 보여 준다. 관세사 이력 비율이 높은 다른 갈래로 바로 옮겨 볼 수 있다.
 *
 * 조건은 서버가 관세율표 원문을 옮긴 검수 트리(조건식·소호 원문·국내 결정표)에서만 읽는다. 상태는 사용자가 답한 사실로만
 * 매기고(✓ 맞음 · ✕ 어긋남 · ? 확인 필요), 카드는 판정·저장을 하지 않는다. 답은 지도 위의 질문에서 한다.
 */
const BLUE = "#1d4ed8";
const LINE = "var(--audit-border, #d8dee6)";
const MUTED = "var(--audit-text-muted, #5b6573)";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const LEVEL_LABEL = { chapter: "류", hs4: "호", hs6: "소호", kr10: "국내 10자리" };

export function formatCode(code) {
  const d = String(code || "").replace(/\D/g, "");
  if (d.length === 2) return `제${d}류`;
  if (d.length === 4) return `제${d}호`;
  if (d.length === 6) return `${d.slice(0, 4)}.${d.slice(4)}`;
  if (d.length === 10) return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
  return d;
}

function mark(state) {
  if (state === "true") return { icon: "✓", color: BLUE, label: "맞음" };
  if (state === "false") return { icon: "✕", color: "#475569", label: "어긋남" };
  if (state === "invalid") return { icon: "✕", color: "#475569", label: "값 범위 밖" };
  if (state === "not_applicable") return { icon: "–", color: MUTED, label: "해당 없음" };
  return { icon: "?", color: MUTED, label: "확인 필요" };
}

function answerText(row) {
  if (row.answer === undefined || row.answer === null) return "";
  if (row.kind === "flow" && !row.numeric) return `답: ${row.value_labels?.[row.answer] || valueLabel(row.answer, row.fact_key)}`;
  if (row.kind === "flow") return `답: ${typeof row.answer === "number" ? withUnit(row.answer, row.unit) : row.answer}`;
  const unit = row.expected?.unit ? String(row.expected.unit).trim() : "";
  const shown = row.value_labels?.[row.answer] || (typeof row.answer === "string" ? valueLabel(row.answer, row.fact_key) : row.answer);
  // 관측 답에서 계산한 법률 사실(예: 부분품 → 제8708호 부분품 요건)은 사용자가 직접 고른 값이 아님을 밝힌다(2026-10-15).
  const derived = row.input_origin === "derived_from_product_observations" ? " (답에서 계산)" : "";
  return `답: ${shown}${unit && typeof row.answer === "number" ? unit : ""}${derived}`;
}

// 규칙 레지스트리 줄 — 값은 원래 코드로 오므로 기존 한글 라벨로 바꾼다. 수치는 원문 경계 그대로(양 끝 모두 적는다).
// 와트·볼트암페어는 1,000 이상이면 킬로 단위로(75000 W → 75 kW). 값 자체는 원문 경계 그대로다.
export function withUnit(v, unit) {
  const n = Number(v);
  if ((unit === "W" || unit === "VA") && Number.isFinite(n) && Math.abs(n) >= 1000) return `${n / 1000} k${unit}`;
  return unit ? `${v} ${unit}` : `${v}`;
}

function intervalText(iv, unit) {
  const lo = iv.min !== undefined ? `${withUnit(iv.min, unit)} ${iv.min_inclusive ? "이상" : "초과"}` : "";
  const hi = iv.max !== undefined ? `${withUnit(iv.max, unit)} ${iv.max_inclusive ? "이하" : "미만"}` : "";
  return [lo, hi].filter(Boolean).join(" ~ ") || "값이 있음";
}

function valuesText(key, values, own = null) {
  const labels = (Array.isArray(values) ? values : []).map((v) => (own && own[v]) || valueLabel(v, key));
  if (labels.length > 4) return `다음 중 하나: ${labels.slice(0, 4).join(" · ")} 외 ${labels.length - 4}개`;
  return labels.length > 1 ? `다음 중 하나: ${labels.join(" · ")}` : labels.join("");
}

export function flowText(r, list = "required") {
  // 팩이 준 라벨(fact_label·value_labels)을 먼저, 없으면 기존 라벨
  const name = r.fact_label || factLabel(r.fact_key);
  if (list === "exclusions" && r.any_value) return `${name}: 무엇이든 답하면(모름 제외)`;
  const what = r.numeric
    ? (r.intervals || []).map((iv) => intervalText(iv, r.unit)).join(" 또는 ")
    : valuesText(r.fact_key, list === "required" ? r.allowed_values : r.values, r.value_labels);
  return `${name}: ${what}`;
}

/**
 * @param code        고른 마디(호·소호·국내 10자리)
 * @param answersKey  답한 사실이 바뀌면 다시 불러오도록(표시용 키)
 * @param fetchConditions (code) => Promise<서버 응답>
 * @param labelFor    (code) => 한글 이름(지도 마디에서) | ""
 * @param historyFor  (code) => { share } | null — 관세사 이력 비율
 * @param alternatives [{ code, share }] 이력 비율이 높은 다른 갈래
 * @param canSelect   (code) => 지도에 그 마디가 있어 고를 수 있는가
 * @param onSelect    (code) => 지도에서 그 마디를 고른다
 */
export default function TargetConditionsCard({ code, answersKey = "", fetchConditions, labelFor = () => "", historyFor = () => null, alternatives = [], canSelect = () => false, onSelect = null }) {
  const [state, setState] = useState({ code: null, loading: false, data: null, error: false });
  useEffect(() => {
    if (!code || !fetchConditions) return undefined;
    let alive = true;
    setState({ code, loading: true, data: null, error: false });
    Promise.resolve(fetchConditions(code))
      .then((data) => { if (alive) setState({ code, loading: false, data, error: !data }); })
      .catch(() => { if (alive) setState({ code, loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, [code, answersKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!code || !fetchConditions) return null;
  const data = state.code === code ? state.data : null;
  const hist = historyFor(code);
  const required = Array.isArray(data?.required) ? data.required : [];
  const exclusions = Array.isArray(data?.exclusions) ? data.exclusions : [];
  const known = required.filter((r) => r.kind !== "legal_text" && r.state === "true").length;
  const open = required.filter((r) => r.kind !== "legal_text" && (r.state === "unknown" || !r.state)).length;
  const off = required.filter((r) => r.state === "false" || r.state === "invalid").length;
  const textOf = (r) => {
    if (r.kind === "legal_text") return labelFor(r.code) || r.text;
    if (r.kind === "flow") return flowText(r, "required");
    return r.text;
  };
  const exclusionText = (x) => (x.kind === "flow" ? flowText(x, "exclusions") : x.text);
  // 다른 물품 종류에서만 묻는 세부 질문(무엇을 답해도 이 세번이 아니게 되는 줄)은 한 줄로 묶는다 — 답해서 걸린 줄은 따로 보인다.
  const otherKindRows = exclusions.filter((x) => x.kind === "flow" && x.any_value && !(x.destinations || []).length && x.state !== "true");
  const shownExclusions = otherKindRows.length > 1 ? exclusions.filter((x) => !otherKindRows.includes(x)) : exclusions;
  const destButton = (d) => {
    const h = historyFor(d);
    const label = labelFor(d);
    const content = <>{formatCode(d)}{h && h.share > 0 ? <span style={{ color: MUTED, fontSize: 11 }}> · 이력 {Math.round(h.share)}%</span> : null}</>;
    return onSelect && canSelect(d)
      ? <button key={d} type="button" title={label || undefined} onClick={() => onSelect(d)} style={{ border: 0, background: "none", padding: 0, color: BLUE, cursor: "pointer", fontFamily: "inherit", fontSize: 12, textDecoration: "underline", textUnderlineOffset: 2 }}>{content}</button>
      : <span key={d} title={label || undefined} style={{ fontSize: 12 }}>{content}</span>;
  };
  return (
    <section data-testid="target-conditions" data-code={code} aria-label="필수·배제 조건" style={{ marginTop: 10, border: `1px solid ${LINE}`, borderRadius: 8, background: "var(--audit-elev, #fff)", padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <b style={{ fontSize: 15, color: BLUE, fontFamily: MONO }}>{formatCode(code)}</b>
        <b style={{ fontSize: 13 }}>이(가) 되려면</b>
        {hist && hist.share > 0 && <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: "#eef3fd", color: BLUE, fontWeight: 600 }}>관세사 이력 {Math.round(hist.share)}%</span>}
        <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: "#eef0f3", color: "#3b4452" }}>조건부 · 확정 아님</span>
        {data?.supported && <span data-testid="target-conditions-counts" style={{ marginLeft: "auto", fontSize: 12, color: MUTED }}>맞음 {known} · 확인 필요 {open}{off ? ` · 어긋남 ${off}` : ""}</span>}
      </div>
      {alternatives.length > 0 && (
        <div data-testid="target-conditions-alternatives" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, fontSize: 12 }}>
          <span style={{ color: MUTED }}>다른 갈래 · 관세사 이력</span>
          {alternatives.map((a) => (
            <button key={a.code} type="button" onClick={() => onSelect && onSelect(a.code)} style={{ border: `1px solid ${a.code === code ? BLUE : LINE}`, background: a.code === code ? "#eef3fd" : "transparent", color: a.code === code ? BLUE : "inherit", borderRadius: 999, padding: "1px 9px", fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>
              <span style={{ fontFamily: MONO }}>{formatCode(a.code)}</span> {Math.round(a.share)}%
            </button>
          ))}
        </div>
      )}
      {state.loading && <div role="status" style={{ marginTop: 8, fontSize: 12, color: MUTED }}>조건을 불러오는 중…</div>}
      {!state.loading && state.error && <div role="alert" style={{ marginTop: 8, fontSize: 12, color: MUTED }}>조건을 불러오지 못했습니다.</div>}
      {!state.loading && data && data.supported === false && (
        <div data-testid="target-conditions-unsupported" style={{ marginTop: 8, fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
          {data.reason === "code_not_in_domestic_table" || data.reason === "code_not_in_tariff" ? "이 10자리는 현행 관세율표에 없는 세번입니다." : "이 류는 조건을 한곳에 모은 공통 트리 형식이 아니라 아직 필수·배제 조건을 보여 주지 못합니다. 위 지도의 질문과 \"이 세번이 되려면\" 목록을 보세요."}
        </div>
      )}
      {!state.loading && data?.supported && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginTop: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>필수 조건 — 모두 맞아야 이 세번</div>
            <ol data-testid="target-conditions-required" style={{ listStyle: "none", margin: 0, padding: 0, borderTop: `1px solid ${LINE}` }}>
              {required.map((r, i) => {
                const m = r.kind === "legal_text" ? { icon: "§", color: MUTED, label: "원문" } : r.kind === "consistency" ? { icon: "?", color: MUTED, label: "정합성 확인" } : mark(r.state);
                return (
                  <li key={`${r.level}-${i}`} data-state={r.state} data-kind={r.kind || "condition"} style={{ display: "grid", gridTemplateColumns: "20px 64px minmax(0,1fr) auto", columnGap: 8, padding: "7px 0", borderBottom: `1px solid ${LINE}`, fontSize: 12, alignItems: "baseline" }}>
                    <span aria-label={m.label} style={{ color: m.color, fontWeight: 700 }}>{m.icon}</span>
                    <span style={{ color: MUTED, fontSize: 11 }}>{LEVEL_LABEL[r.level] || r.level}{r.assumed_path ? " · 가정" : ""}</span>
                    <span style={{ lineHeight: 1.45, overflowWrap: "anywhere", color: r.kind === "legal_text" ? MUTED : "inherit" }}>{textOf(r)}</span>
                    <span style={{ fontSize: 11, color: r.state === "true" ? BLUE : MUTED, whiteSpace: "nowrap" }}>{r.kind === "legal_text" ? "원문 문구" : r.state === "invalid" ? `${answerText(r)} · 값 범위 밖` : answerText(r) || m.label}</span>
                  </li>
                );
              })}
            </ol>
            {data.domestic === "no_table" && String(code).length === 10 && <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>이 소호에는 국내 결정표가 없어 10자리 조건은 확인하지 못했습니다.</div>}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>배제 조건 — 하나라도 해당하면 다른 곳으로</div>
            {exclusions.length === 0 && <div style={{ fontSize: 12, color: MUTED }}>트리에 적힌 배제 조건이 없습니다.</div>}
            <ul data-testid="target-conditions-exclusions" style={{ listStyle: "none", margin: 0, padding: 0, borderTop: exclusions.length ? `1px solid ${LINE}` : 0 }}>
              {shownExclusions.map((x, i) => (
                <li key={`x-${i}`} data-state={x.state} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 16px minmax(0, 46%)", columnGap: 8, padding: "7px 0", borderBottom: `1px solid ${LINE}`, fontSize: 12, alignItems: "baseline" }}>
                  <span style={{ lineHeight: 1.45, overflowWrap: "anywhere", fontWeight: x.state === "true" ? 700 : 400 }}>{exclusionText(x)}{x.state === "true" ? <span style={{ color: BLUE, fontSize: 11 }}> · 지금 답과 맞음</span> : null}</span>
                  <span aria-hidden="true" style={{ color: MUTED }}>→</span>
                  <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {(x.destinations || []).length ? x.destinations.map(destButton) : <span style={{ color: MUTED, fontSize: 12 }}>이 세번 밖</span>}
                    {x.outside_scope && (x.destinations || []).length ? <span style={{ color: MUTED, fontSize: 11 }}>(이 슬라이스 밖)</span> : null}
                    {x.more_destinations ? <span style={{ color: MUTED, fontSize: 11 }}>외 {x.more_destinations}곳</span> : null}
                  </span>
                </li>
              ))}
              {otherKindRows.length > 1 && (
                <li data-testid="target-conditions-other-kinds" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 16px minmax(0, 46%)", columnGap: 8, padding: "7px 0", borderBottom: `1px solid ${LINE}`, fontSize: 12, alignItems: "baseline" }}>
                  <span style={{ lineHeight: 1.45, overflowWrap: "anywhere" }} title={otherKindRows.map((x) => x.fact_label || factLabel(x.fact_key)).join(" · ")}>
                    다른 물품 종류의 세부 질문 {otherKindRows.length}개 — 무엇이든 답하면
                    <span style={{ display: "block", color: MUTED, fontSize: 11 }}>{otherKindRows.map((x) => x.fact_label || factLabel(x.fact_key)).join(" · ")}</span>
                  </span>
                  <span aria-hidden="true" style={{ color: MUTED }}>→</span>
                  <span style={{ color: MUTED, fontSize: 12 }}>이 세번 밖</span>
                </li>
              )}
            </ul>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>도착 세번은 그곳의 다른 조건까지 맞아야 실제로 그 세번이 됩니다.</div>
          </div>
        </div>
      )}
      {!state.loading && data?.supported && (
        <div style={{ fontSize: 11, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
          조건은 관세율표 원문을 옮긴 결정트리에서 읽습니다. 수치는 원문 경계(이상·초과)를 그대로 씁니다. 답은 위 지도의 질문에서 하며, 모두 확인되기 전까지 결과는 조건부입니다.
          {data.other_consistency_checks ? ` 이 밖에 류 고유의 정합성 확인 ${data.other_consistency_checks}개가 있습니다.` : ""}
          {data.source === "registry_pack" ? " 이 류는 조건을 규칙 레지스트리(원문을 옮긴 결정 흐름도)에서 읽습니다. 필수 조건은 어느 경로로 가든 맞아야 하는 것만 보입니다." : ""}
        </div>
      )}
    </section>
  );
}
